import { useEffect, useState } from "react";
import BottomNavigation from "../components/BottomNavigation";
import AppHeader from "../components/AppHeader";
import PrimaryButton from "../components/PrimaryButton";
import { t, tNode } from "../services/i18n";
import { track } from "../services/analytics";
import { getAppState, recordSession, toLocalDate } from "../services/storage";
import {
  getSessionsForWeek,
  getCurrentStreak,
  getLastSession,
  currentWeekDays,
  currentMonthDays,
  sessionsPerDay,
  completedDays,
  isTodayCompleted
} from "../services/routine";

export default function RoutinePage() {
  const [state, setState] = useState(() => getAppState());
  const [chartView, setChartView] = useState<"week" | "month">("week");
  useEffect(() => { track("routine_viewed"); }, []);

  const now = new Date();
  const week = currentWeekDays(now);
  const done = completedDays(state.sessions);
  const weekSessions = getSessionsForWeek(state.sessions, now);
  const streak = getCurrentStreak(state.sessions, now);
  const last = getLastSession(state.sessions);
  const todayDone = isTodayCompleted(state.sessions, now);
  const today = toLocalDate(now);
  const weekdayLabels = tNode<string[]>("routine.weekdays", ["M","T","W","T","F","S","S"]);

  const lastLabel = (() => {
    if (!last) return t("routine.noSessions");
    const d = new Date(last.completedAt);
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (last.localDate === today) return `${t("routine.today")}, ${time}`;
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    if (last.localDate === toLocalDate(yesterday)) return `${t("routine.yesterday")}, ${time}`;
    return d.toLocaleDateString([], { day: "numeric", month: "short" }) + `, ${time}`;
  })();

  const markToday = () => {
    setState(recordSession());
    track("session_marked_complete");
  };

  return (
    <>
      <AppHeader title={t("routine.title")} />
      <div className="page page--with-nav">
        <div className="card stat-row">
          <div>
            <div className="stat-row__label">{t("routine.thisWeek")}</div>
            <div className="stat-row__value">{weekSessions.length}</div>
            <div className="stat-row__label">{t("routine.sessionsCompleted")}</div>
          </div>
          <span style={{ color: "var(--color-accent-green)", fontSize: 28 }} aria-hidden="true">&#10003;</span>
        </div>

        <div className="card stat-row">
          <div>
            <div className="stat-row__label">{t("routine.lastSession")}</div>
            <div style={{ fontWeight: 600 }}>{lastLabel}</div>
          </div>
        </div>

        <div className="card">
          <div className="stat-row">
            <div>
              <div className="stat-row__label">{t("routine.currentStreak")}</div>
              <div className="stat-row__value">
                {streak} {streak === 1 ? t("routine.day") : t("routine.days")}
              </div>
            </div>
          </div>
          <div className="week-grid" role="list" aria-label={t("routine.thisWeek")}>
            {week.map((d, i) => {
              const isDone = done.has(d);
              const isToday = d === today;
              return (
                <div className="week-day" role="listitem" key={d}>
                  <span>{weekdayLabels[i]}</span>
                  <span
                    className={
                      "week-day__mark" +
                      (isDone ? " week-day__mark--done" : "") +
                      (!isDone && isToday ? " week-day__mark--today" : "")
                    }
                    aria-label={d + (isDone ? " completed" : "")}
                  >
                    {isDone ? "\u2713" : isToday ? "\u2022" : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="stat-row">
            <div className="stat-row__label">{t("routine.activity")}</div>
            <div className="seg" role="tablist" aria-label={t("routine.activity")}>
              <button
                role="tab"
                aria-selected={chartView === "week"}
                className={chartView === "week" ? "active" : ""}
                onClick={() => setChartView("week")}
              >
                {t("routine.week")}
              </button>
              <button
                role="tab"
                aria-selected={chartView === "month"}
                className={chartView === "month" ? "active" : ""}
                onClick={() => setChartView("month")}
              >
                {t("routine.month")}
              </button>
            </div>
          </div>
          {(() => {
            const days = chartView === "week" ? week : currentMonthDays(now);
            const counts = sessionsPerDay(state.sessions, days);
            const max = Math.max(1, ...counts);
            return (
              <div className="chart">
                {days.map((d, i) => {
                  const isToday = d === today;
                  const label =
                    chartView === "week"
                      ? weekdayLabels[i]
                      : (i === 0 || (i + 1) % 5 === 0) ? String(i + 1) : "";
                  return (
                    <div className="chart__col" key={d}>
                      <span
                        className={
                          "chart__bar" +
                          (counts[i] > 0 ? " chart__bar--filled" : "") +
                          (isToday ? " chart__bar--today" : "")
                        }
                        style={{ height: `${8 + (counts[i] / max) * 56}px` }}
                        title={`${d}: ${counts[i]}`}
                      />
                      <span className="chart__label">{label}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        <div className="mt-24">
          <PrimaryButton
            label={todayDone ? t("routine.todayDone") : t("routine.markToday")}
            onClick={markToday}
            disabled={todayDone}
          />
        </div>
      </div>
      <BottomNavigation />
    </>
  );
}
